/**
 * PlanTrainTrack — phase navigator strip in the project header.
 *
 * NOT a graph. The full plan DAG lives in the plan view (PlanGraphView /
 * EvidenceBoard); this strip exists for orientation + jumping while the user
 * is inside a workspace. Plan nodes are grouped into the five pipeline phases
 * (Context · Research · Build · Documents · Package, from workspace_type);
 * each segment shows a done/total count and a thin progress underline, and
 * clicking a segment opens a compact dropdown listing that phase's
 * workspaces. Selecting one navigates exactly like the old chip click.
 *
 * This replaces the previous mini-DAG chip row, whose "few parallel
 * branches" layout assumption collapsed on real plans (a 16-node plan put
 * 12 documentation chips in one column with crossing connectors).
 *
 * One row, fixed height, never stacks. Single chevron between segments.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useProjectStore } from '../../stores/projectStore';
import { getReportLibraryUrl, authHeaders } from '../../apiService';

interface PlanNode {
  node_id: string;
  label: string;
  validation_status?: string;
  workspace_id?: string;
  workspace_type?: string;
  deliverable?: string;
  is_terminal?: boolean;
}

const STRIP_H = 36;       // fixed strip height — never grows with plan size
const SEGMENT_H = 28;     // clickable segment height inside the strip
const ACTIVE_NAME_MAX = 24; // truncation for the active workspace name

/** Pipeline phases in canonical order. Nodes whose workspace_type matches
 *  none of these group under a trailing "Other" segment. */
const PHASES: { key: string; label: string }[] = [
  { key: 'institution_context', label: 'Context' },
  { key: 'research', label: 'Research' },
  { key: 'production', label: 'Build' },
  { key: 'documentation', label: 'Documents' },
  { key: 'output_package', label: 'Package' },
];
const OTHER_KEY = '__other__';

function truncate(s: string, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

function isNodeComplete(node: PlanNode): boolean {
  return (
    node.validation_status === 'approved' ||
    node.validation_status === 'passed' ||
    node.validation_status === 'complete' ||
    node.is_terminal === true
  );
}

/** Topological order (Kahn's algorithm) so dropdown rows follow pipeline
 *  order rather than object-key insertion order. Cycle leftovers are
 *  appended deterministically. */
function topoOrder(
  nodes: Record<string, PlanNode>,
  edges: Record<string, string[]>,
): string[] {
  const ids = Object.keys(nodes);
  const idSet = new Set(ids);
  const outgoing: Record<string, string[]> = {};
  const indeg: Record<string, number> = {};
  for (const id of ids) { outgoing[id] = []; indeg[id] = 0; }
  for (const [src, targets] of Object.entries(edges)) {
    if (!idSet.has(src)) continue;
    for (const t of targets) {
      if (idSet.has(t)) { outgoing[src].push(t); indeg[t]++; }
    }
  }
  const order: string[] = [];
  const placed = new Set<string>();
  const queue = ids.filter(id => indeg[id] === 0);
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    placed.add(id);
    for (const t of outgoing[id]) {
      indeg[t]--;
      if (indeg[t] === 0) queue.push(t);
    }
  }
  for (const id of ids) if (!placed.has(id)) order.push(id);
  return order;
}

interface PhaseSegment {
  key: string;
  label: string;
  nodes: PlanNode[];
  done: number;
}

function buildSegments(
  nodes: Record<string, PlanNode>,
  edges: Record<string, string[]>,
): PhaseSegment[] {
  const order = topoOrder(nodes, edges);
  const byPhase: Record<string, PlanNode[]> = {};
  for (const id of order) {
    const node = nodes[id];
    const phase = PHASES.find(p => p.key === node.workspace_type)?.key ?? OTHER_KEY;
    (byPhase[phase] ||= []).push(node);
  }
  const segments: PhaseSegment[] = [];
  for (const p of PHASES) {
    const phaseNodes = byPhase[p.key];
    if (!phaseNodes || phaseNodes.length === 0) continue; // empty phases add noise, skip
    segments.push({
      key: p.key,
      label: p.label,
      nodes: phaseNodes,
      done: phaseNodes.filter(isNodeComplete).length,
    });
  }
  const other = byPhase[OTHER_KEY];
  if (other && other.length > 0) {
    segments.push({
      key: OTHER_KEY,
      label: 'Other',
      nodes: other,
      done: other.filter(isNodeComplete).length,
    });
  }
  return segments;
}

function nodeTooltip(node: PlanNode): string {
  const displayLabel = node.label || node.deliverable || node.node_id;
  return node.deliverable && node.label && node.deliverable !== node.label
    ? `${node.label} — ${node.deliverable}`
    : displayLabel;
}

/** Status dot reusing the existing chip dot language: green = complete,
 *  navy = active, hollow hairline = pending. */
function StatusDot({ isActive, isDone }: { isActive: boolean; isDone: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="flex-shrink-0 rounded-full"
      style={{
        width: 7,
        height: 7,
        background: isActive
          ? 'var(--primary)'
          : isDone
            ? 'var(--success)'
            : 'transparent',
        border: isActive || isDone ? 'none' : '1.5px solid var(--border-strong)',
      }}
    />
  );
}

function SegmentChevron() {
  return (
    <svg
      className="flex-shrink-0 text-fg-tertiary mx-0.5"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default function PlanTrainTrack() {
  const activeProject = useProjectStore(s => s.activeProject);
  const dictWorkspace = useProjectStore(s => s.dictWorkspace);
  const setWorkspaceMode = useProjectStore(s => s.setWorkspaceMode);
  const loadSpecificWorkspace = useProjectStore(s => s.loadSpecificWorkspace);

  const [planData, setPlanData] = useState<{
    nodes: Record<string, PlanNode>;
    edges: Record<string, string[]>;
  } | null>(null);
  const [openPhase, setOpenPhase] = useState<string | null>(null);
  // The open segment button's viewport rect — the dropdown renders through a
  // portal at fixed coordinates because the header mount wraps this strip in
  // overflow-hidden (App.tsx), which silently clipped an in-flow menu to the
  // 36px strip ("clicking does nothing").
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const projectId = activeProject?.project_id;
  const planWorkspaceId = projectId ? `ws_${projectId}` : null;
  const isBuild = activeProject?.project_type === 'build';

  useEffect(() => {
    if (!isBuild || !planWorkspaceId) return;

    const toPlanNodes = (raw: Record<string, unknown>): Record<string, PlanNode> => {
      const planNodes: Record<string, PlanNode> = {};
      for (const [nid, node] of Object.entries(raw)) {
        const n = node as any;
        if (n.workspace_type || n.deliverable) {
          planNodes[nid] = {
            node_id: nid,
            label: n.label || nid,
            validation_status: n.validation_status,
            workspace_id: n.workspace_id,
            workspace_type: n.workspace_type,
            deliverable: n.deliverable,
            is_terminal: n.is_terminal,
          };
        }
      }
      return planNodes;
    };

    if (
      dictWorkspace &&
      dictWorkspace.workspace_id === planWorkspaceId &&
      Object.keys(dictWorkspace.nodes || {}).length > 0
    ) {
      const planNodes = toPlanNodes(dictWorkspace.nodes);
      if (Object.keys(planNodes).length > 0) {
        setPlanData({ nodes: planNodes, edges: dictWorkspace.edges || {} });
        return;
      }
    }

    const userId = activeProject?.user_id || '';
    if (!userId) return;

    const url = getReportLibraryUrl();
    // BAR-750: report-library's /workspaces/{id}/state is auth-gated — a tokenless
    // fetch 401s under ENFORCE_AUTH and the catch below swallows it, leaving the
    // train-track silently empty. Send the bearer token.
    authHeaders()
      .then(headers => fetch(`${url}/workspaces/${planWorkspaceId}/state?user_id=${encodeURIComponent(userId)}`, { headers }))
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (data.success && data.nodes) {
          const planNodes = toPlanNodes(data.nodes);
          if (Object.keys(planNodes).length > 0) {
            setPlanData({ nodes: planNodes, edges: data.edges || {} });
          }
        }
      })
      .catch(err => console.error('[PlanTrainTrack] Failed:', err));
  }, [dictWorkspace?.workspace_id, planWorkspaceId, isBuild, activeProject?.user_id]);

  // Close the dropdown on outside click, Escape, scroll, or resize. The menu
  // lives in a portal, so the outside-click check must include menuRef (it is
  // NOT inside rootRef); scroll/resize close rather than reposition — the
  // anchor rect is captured once at open.
  useEffect(() => {
    if (!openPhase) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpenPhase(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenPhase(null);
    };
    const onScrollOrResize = () => setOpenPhase(null);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [openPhase]);

  const segments = useMemo(
    () => (planData ? buildSegments(planData.nodes, planData.edges) : []),
    [planData],
  );

  if (!isBuild || !planData || segments.length === 0) return null;

  const activeWorkspaceId =
    dictWorkspace?.workspace_id && dictWorkspace.workspace_id !== planWorkspaceId
      ? dictWorkspace.workspace_id
      : null;

  const drillIntoNode = async (node: PlanNode) => {
    // Await loadSpecificWorkspace before switching mode. Previously these
    // ran in parallel: setWorkspaceMode flipped the active phase
    // synchronously while loadSpecificWorkspace was still in flight, so
    // RequirementsPhase / ProcessViewer rendered against a stale
    // dictWorkspace (still pointing at the plan workspace). The user
    // saw the plan graph rendered as if it were the spawned workspace.
    // Worse, if loadSpecificWorkspace silently failed (returned false),
    // dictWorkspace never updated and the user was stuck on the stale
    // plan view with no recovery.
    const wsId = node.workspace_id;
    const wsType = node.workspace_type;
    const uid = activeProject?.user_id;
    if (!wsId || !uid || !wsType) return;

    const loaded = await loadSpecificWorkspace(wsId, uid);
    if (!loaded) {
      console.warn(
        '[PlanTrainTrack] loadSpecificWorkspace returned false for',
        wsId,
        '— aborting navigation rather than leaving the user on a stale view.'
      );
      return;
    }

    if (wsType === 'research') setWorkspaceMode('research', 'user');
    else if (wsType === 'production') setWorkspaceMode('production', 'user');
    else if (wsType === 'institution_context') setWorkspaceMode('institution_context', 'user');
    else if (wsType === 'documentation') setWorkspaceMode('documentation', 'user');
    else if (wsType === 'output_package') setWorkspaceMode('output_package', 'user');
  };

  const activeNode = activeWorkspaceId
    ? Object.values(planData.nodes).find(n => n.workspace_id === activeWorkspaceId) ?? null
    : null;

  return (
    <div
      ref={rootRef}
      className="flex items-center min-w-0"
      style={{ height: STRIP_H }}
      aria-label="Plan phases"
    >
      {segments.map((seg, i) => {
        const isActivePhase = Boolean(
          activeNode && seg.nodes.some(n => n.node_id === activeNode.node_id)
        );
        const isOpen = openPhase === seg.key;
        const pct = seg.nodes.length > 0 ? (seg.done / seg.nodes.length) * 100 : 0;
        const alignRight = i >= segments.length / 2;

        return (
          <React.Fragment key={seg.key}>
            {i > 0 && <SegmentChevron />}
            <div className="relative min-w-0 flex-shrink">
              <button
                type="button"
                onClick={(e) => {
                  if (isOpen) { setOpenPhase(null); return; }
                  // One workspace in the phase -> navigate directly; a list of
                  // one is a pointless extra click. Multiple -> dropdown.
                  const clickable = seg.nodes.filter(
                    n => n.workspace_id && n.workspace_type && n.workspace_id !== activeWorkspaceId,
                  );
                  if (seg.nodes.length === 1 && clickable.length === 1) {
                    void drillIntoNode(clickable[0]);
                    return;
                  }
                  setAnchorRect(e.currentTarget.getBoundingClientRect());
                  setOpenPhase(seg.key);
                }}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                title={`${seg.label}: ${seg.done} of ${seg.nodes.length} complete`}
                className={`relative flex items-center gap-1.5 px-2.5 rounded transition-colors min-w-0 max-w-full ${
                  isActivePhase
                    ? 'bg-primary-tint text-fg'
                    : isOpen
                      ? 'bg-bg-warm text-fg-secondary'
                      : 'text-fg-secondary hover:bg-bg-warm'
                }`}
                style={{ height: SEGMENT_H }}
              >
                <span className={`text-xs whitespace-nowrap ${isActivePhase ? 'font-semibold' : 'font-medium'}`}>
                  {seg.label}
                </span>
                {isActivePhase && activeNode && (
                  <span className="text-xs text-fg-secondary truncate min-w-0">
                    · {truncate(activeNode.label || activeNode.deliverable || activeNode.node_id, ACTIVE_NAME_MAX)}
                  </span>
                )}
                <span
                  className="text-[11px] text-fg-tertiary whitespace-nowrap"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {seg.done}/{seg.nodes.length}
                </span>
                {/* Thin progress underline */}
                <span
                  aria-hidden="true"
                  className="absolute left-2.5 right-2.5 bottom-0 rounded-full overflow-hidden pointer-events-none"
                  style={{ height: 2, background: 'var(--border-default)' }}
                >
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${pct}%`, background: 'var(--success)' }}
                  />
                </span>
              </button>

              {isOpen && anchorRect && createPortal(
                <div
                  ref={menuRef}
                  role="menu"
                  aria-label={`${seg.label} workspaces`}
                  className="fixed z-[1000] bg-surface border border-border-default rounded-lg shadow-bark-md py-1 overflow-y-auto"
                  style={{
                    top: anchorRect.bottom + 6,
                    ...(alignRight
                      ? { right: Math.max(8, window.innerWidth - anchorRect.right) }
                      : { left: Math.max(8, anchorRect.left) }),
                    minWidth: 240,
                    maxWidth: 320,
                    maxHeight: 320,
                  }}
                >
                  {seg.nodes.map(node => {
                    const isActive = Boolean(
                      activeWorkspaceId && node.workspace_id === activeWorkspaceId
                    );
                    const isDone = isNodeComplete(node);
                    const isClickable = Boolean(node.workspace_id && node.workspace_type) && !isActive;
                    return (
                      <button
                        key={node.node_id}
                        role="menuitem"
                        type="button"
                        disabled={!isClickable}
                        onClick={() => {
                          setOpenPhase(null);
                          void drillIntoNode(node);
                        }}
                        title={nodeTooltip(node)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                          isActive
                            ? 'bg-primary-tint'
                            : isClickable
                              ? 'hover:bg-bg-warm'
                              : 'opacity-60 cursor-default'
                        }`}
                      >
                        <StatusDot isActive={isActive} isDone={isDone} />
                        <span className="min-w-0 flex-1">
                          <span className={`block text-xs truncate ${isActive ? 'font-semibold text-fg' : 'text-fg'}`}>
                            {node.label || node.deliverable || node.node_id}
                          </span>
                          {node.deliverable && node.deliverable !== node.label && (
                            <span className="block text-[11px] text-fg-tertiary truncate">
                              {node.deliverable}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>,
                document.body,
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
